"use client";

import { useEffect, useRef } from "react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { cn } from "@/utils/general";

import { HomepageHeroImageLayoutProps } from "./HomepageHeroImage.types";
import HomepageHeroImageTabs from "./HomepageHeroImageTabs";

gsap.registerPlugin(ScrollTrigger);

const HomepageHeroImageDesktop = ({
  data,
  active,
  setActive,
  benefitsSection,
  imagesSection,
}: HomepageHeroImageLayoutProps) => {
  const { subheading, tabs } = data;
  const dividerRef = useRef<HTMLDivElement>(null);
  const tabRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const divider = dividerRef.current;
    const tab = tabRef.current;
    if (!divider || !tab) return;

    const tween = gsap.fromTo(
      divider,
      { width: 0 },
      {
        /* Overshoot by the bullet size so the bullet parks behind the tab. */
        width: () =>
          `${tab.getBoundingClientRect().left - divider.getBoundingClientRect().left + 8}px`,
        duration: 1.2,
        ease: "power1.inOut",
        scrollTrigger: {
          trigger: divider,
          start: "bottom 80%",
          toggleActions: "play none none none",
          invalidateOnRefresh: true,
        },
      }
    );

    return () => {
      tween.kill();
    };
  }, []);

  return (
    <div className="mx-auto flex h-[763px] w-full max-w-[1600px] px-4 md:px-10 min-[1470px]:px-0">
      <div className="flex h-[508px] w-1/2 items-start pt-[118px]">
        {/* z-index makes this a stacking context so the negative-z connector clears the section bg. */}
        <div className="relative z-[1] mx-auto w-[360px]">
          <div
            ref={dividerRef}
            className="absolute left-full top-1/2 z-[2] h-px bg-nova-transparency-black-20"
          >
            <span className="absolute -right-1 -top-1 size-2 rounded-full bg-nova-brand-accent-80" />
          </div>
          {imagesSection}
        </div>
      </div>
      <div className="flex h-[360px] w-1/2 items-start pt-[84px]  pr-0 min-[1470px]:pr-[112px]">
        <div className="box-border max-w-[556px] pr-6 min-[1470px]:pr-12">
          <div className="max-w-[527px] grow">
            <div className="max-w-[445px] pb-20">
              <TextElementNova text={subheading} element="h2" className="Title-20-Super-Normal" />
            </div>
            {benefitsSection}
          </div>
        </div>
        <div
          ref={tabRef}
          className={cn(
            "z-[1] min-w-[171px] pt-[122px] self-start  transition-transform duration-500",
            active === "women" && "translate-y-16",
            active === "balance" && "translate-y-0",
            active === "postpartum" && "-translate-y-16"
          )}
        >
          <div className=" ">
            <HomepageHeroImageTabs tabs={tabs} setter={setActive} active={active} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomepageHeroImageDesktop;
